import React, { useState, useEffect } from 'react';
import { FaUser, FaSpinner, FaExclamationCircle, FaChevronRight, FaFileInvoice } from 'react-icons/fa';
import { CreditCard, Calendar, CheckCircle, AlertCircle, XCircle, Clock, Search, FileText, ArrowUpRight, DollarSign, Pocket } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const WithdrawalRecord = ({ userDetails, token }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    status: 'all'
  });

  useEffect(() => {
    const fetchWithdrawals = async () => {
      try {
        const response = await axios.get(
          `https://api.visiontech2030.com/api/withdrawals/history/${userDetails.email}`,
          { headers: { 'Authorization': token } }
        );
        setWithdrawals(response.data);
      } catch (err) {
        setError('Failed to load withdrawal records');
        console.error('Error fetching withdrawals:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWithdrawals();
  }, [userDetails.email, token]);

  const getStatusIcon = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <CheckCircle size={16} className="vt-withdrawal-status-icon approved" />;
      case 'PENDING':
        return <Clock size={16} className="vt-withdrawal-status-icon pending" />;
      case 'REJECTED':
        return <XCircle size={16} className="vt-withdrawal-status-icon rejected" />;
      default:
        return <AlertCircle size={16} className="vt-withdrawal-status-icon unknown" />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'Congratulations! Withdrawal successful';
      case 'PENDING':
        return 'Withdrawal request is being processed';
      case 'REJECTED':
        return 'Withdrawal request was declined';
      default:
        return '';
    }
  };

  const calculateTotalWithdrawals = () => {
    return withdrawals
      .filter(w => w.withdrawalStatus.toUpperCase() === 'APPROVED')
      .reduce((total, w) => total + w.amountToReceive, 0);
  };

  const filteredWithdrawals = withdrawals.filter(withdrawal => {
    const matchesSearch = search === '' || 
      withdrawal.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
      withdrawal.requestedAmount.toString().includes(search);
    
    const matchesStatus = activeFilters.status === 'all' || 
      withdrawal.withdrawalStatus.toLowerCase() === activeFilters.status.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const EmptyState = () => (
    <div className="vt-withdrawal-empty">
      <div className="vt-withdrawal-empty-icon">
        <CreditCard size={40} />
      </div>
      <h3 className="vt-withdrawal-empty-title">No Withdrawal History</h3>
      <p className="vt-withdrawal-empty-message">
        You haven't made any withdrawal requests yet. Your withdrawal history will appear here.
      </p>
    </div>
  );

  const LoadingState = () => (
    <div className="vt-withdrawal-loading">
      <div className="vt-withdrawal-spinner"></div>
      <p className="vt-withdrawal-loading-text">Loading your withdrawal records...</p>
    </div>
  );

  const ErrorState = () => (
    <div className="vt-withdrawal-error">
      <div className="vt-withdrawal-error-icon">
        <AlertCircle size={32} />
      </div>
      <h3 className="vt-withdrawal-error-title">Could Not Load Records</h3>
      <p className="vt-withdrawal-error-message">{error}</p>
      <button className="vt-withdrawal-retry-button">Try Again</button>
    </div>
  );

  return (
    <div className="vt-withdrawal-container">
      <div className="vt-withdrawal-header">
        <div className="vt-withdrawal-header-decoration"></div>
        <div className="vt-withdrawal-header-icon">
          <Pocket size={20} />
        </div>
        <div className="vt-withdrawal-header-content">
          <h2 className="vt-withdrawal-title">Payout Records</h2>
          <p className="vt-withdrawal-subtitle">Track all your withdrawal activities</p>
        </div>
      </div>
      
      {!isLoading && !error && withdrawals.length > 0 && (
        <div className="vt-withdrawal-summary-card">
          <div className="vt-withdrawal-summary-item">
            <div className="vt-withdrawal-summary-label">Total Withdrawals</div>
            <div className="vt-withdrawal-summary-value">
              KES {calculateTotalWithdrawals().toLocaleString()}
            </div>
          </div>
          <div className="vt-withdrawal-summary-item">
            <div className="vt-withdrawal-summary-label">Transactions</div>
            <div className="vt-withdrawal-summary-value">{withdrawals.length}</div>
          </div>
        </div>
      )}

      {!isLoading && !error && withdrawals.length > 0 && (
        <div className="vt-withdrawal-filters">
          <div className="vt-withdrawal-search">
            <Search size={16} className="vt-withdrawal-search-icon" />
            <input
              type="text"
              placeholder="Search by order number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="vt-withdrawal-search-input"
            />
          </div>
          
          <div className="vt-withdrawal-status-filters">
            <button 
              className={`vt-withdrawal-status-filter ${activeFilters.status === 'all' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'all' }))}
            >
              All
            </button>
            <button 
              className={`vt-withdrawal-status-filter ${activeFilters.status === 'approved' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'approved' }))}
            >
              Approved
            </button>
            <button 
              className={`vt-withdrawal-status-filter ${activeFilters.status === 'pending' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'pending' }))}
            >
              Pending
            </button>
            <button 
              className={`vt-withdrawal-status-filter ${activeFilters.status === 'rejected' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'rejected' }))}
            >
              Rejected
            </button>
          </div>
        </div>
      )}

      <div className="vt-withdrawal-content">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState />
        ) : withdrawals.length === 0 ? (
          <EmptyState />
        ) : filteredWithdrawals.length === 0 ? (
          <div className="vt-withdrawal-no-results">
            <p>No results found for your search criteria</p>
          </div>
        ) : (
          <div className="vt-withdrawal-list">
            {filteredWithdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="vt-withdrawal-card">
                <div className="vt-withdrawal-card-main">
                  <div className="vt-withdrawal-icon-wrapper">
                    <ArrowUpRight 
                      size={18} 
                      className="vt-withdrawal-transaction-icon" 
                    />
                  </div>
                  
                  <div className="vt-withdrawal-info">
                    <div className="vt-withdrawal-amount-row">
                      <span className="vt-withdrawal-amount">
                        <span className="vt-withdrawal-currency">KES</span>
                        {' '}
                        {withdrawal.requestedAmount.toLocaleString()}
                      </span>
                      <div className="vt-withdrawal-status">
                        {getStatusIcon(withdrawal.withdrawalStatus)}
                        <span className={`vt-withdrawal-status-text ${withdrawal.withdrawalStatus.toLowerCase()}`}>
                          {withdrawal.withdrawalStatus}
                        </span>
                      </div>
                    </div>
                    
                    <div className="vt-withdrawal-details-grid">
                      <div className="vt-withdrawal-detail">
                        <Calendar size={14} className="vt-withdrawal-detail-icon" />
                        <span className="vt-withdrawal-date">
                          {format(new Date(withdrawal.requestTime), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="vt-withdrawal-detail">
                        <Clock size={14} className="vt-withdrawal-detail-icon" />
                        <span className="vt-withdrawal-time">
                          {format(new Date(withdrawal.requestTime), 'h:mm a')}
                        </span>
                      </div>
                      <div className="vt-withdrawal-detail">
                        <FileText size={14} className="vt-withdrawal-detail-icon" />
                        <span className="vt-withdrawal-order-number">
                          {withdrawal.orderNumber}
                        </span>
                      </div>
                      <div className="vt-withdrawal-detail">
                        <DollarSign size={14} className="vt-withdrawal-detail-icon" />
                        <span className="vt-withdrawal-receive-amount">
                          Net: KES {withdrawal.amountToReceive.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className={`vt-withdrawal-status-message ${withdrawal.withdrawalStatus.toLowerCase()}`}>
                      {getStatusMessage(withdrawal.withdrawalStatus)}
                    </div>
                  </div>
                  
                  <div className="vt-withdrawal-chevron">
                    <FaChevronRight />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <style jsx>{`
        .vt-withdrawal-container {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .vt-withdrawal-header {
          display: flex;
          align-items: center;
          gap: 16px;
          position: relative;
          margin-bottom: 24px;
        }
        
        .vt-withdrawal-header-decoration {
          position: absolute;
          left: -20px;
          top: -10px;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(126, 58, 242, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          z-index: -1;
        }
        
        .vt-withdrawal-header-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #7e3af2, #9061f9);
          color: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(126, 58, 242, 0.3);
          flex-shrink: 0;
        }
        
        .vt-withdrawal-header-content {
          flex: 1;
        }
        
        .vt-withdrawal-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 4px;
          color: white;
        }
        
        .vt-withdrawal-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        .vt-withdrawal-summary-card {
          display: flex;
          gap: 20px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
        }
        
        .vt-withdrawal-summary-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .vt-withdrawal-summary-label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-withdrawal-summary-value {
          font-size: 18px;
          font-weight: 600;
          color: white;
        }
        
        .vt-withdrawal-filters {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .vt-withdrawal-search {
          position: relative;
        }
        
        .vt-withdrawal-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-withdrawal-search-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 12px 12px 12px 40px;
          color: white;
          font-size: 14px;
          outline: none;
          transition: all 0.2s ease;
        }
        
        .vt-withdrawal-search-input:focus {
          border-color: rgba(126, 58, 242, 0.4);
          box-shadow: 0 0 0 2px rgba(126, 58, 242, 0.2);
        }
        
        .vt-withdrawal-search-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        
        .vt-withdrawal-status-filters {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 4px;
        }
        
        .vt-withdrawal-status-filter {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        
        .vt-withdrawal-status-filter.active {
          background: rgba(126, 58, 242, 0.2);
          border-color: rgba(126, 58, 242, 0.4);
          color: #d8b4fe;
        }
        
        .vt-withdrawal-status-filter:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .vt-withdrawal-content {
          min-height: 200px;
        }
        
        .vt-withdrawal-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .vt-withdrawal-card {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          overflow: hidden;
          transition: all 0.2s ease;
        }
        
        .vt-withdrawal-card:hover {
          transform: translateY(-2px);
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .vt-withdrawal-card-main {
          padding: 16px;
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }
        
        .vt-withdrawal-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(126, 58, 242, 0.1);
          border-radius: 10px;
          color: #d8b4fe;
          flex-shrink: 0;
          margin-top: 4px;
        }
        
        .vt-withdrawal-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        
        .vt-withdrawal-amount-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .vt-withdrawal-amount {
          font-size: 16px;
          font-weight: 600;
          color: white;
        }
        
        .vt-withdrawal-currency {
          color: rgba(255, 255, 255, 0.7);
          font-weight: normal;
          font-size: 14px;
        }
        
        .vt-withdrawal-status {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .vt-withdrawal-status-icon.approved {
          color: #34d399;
        }
        
        .vt-withdrawal-status-icon.pending {
          color: #fbbf24;
        }
        
        .vt-withdrawal-status-icon.rejected {
          color: #f87171;
        }
        
        .vt-withdrawal-status-icon.unknown {
          color: #a1a1aa;
        }
        
        .vt-withdrawal-status-text {
          font-size: 13px;
          font-weight: 500;
        }
        
        .vt-withdrawal-status-text.approved {
          color: #34d399;
        }
        
        .vt-withdrawal-status-text.pending {
          color: #fbbf24;
        }
        
        .vt-withdrawal-status-text.rejected {
          color: #f87171;
        }
        
        .vt-withdrawal-details-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }
        
        .vt-withdrawal-detail {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .vt-withdrawal-detail-icon {
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-withdrawal-date,
        .vt-withdrawal-time,
        .vt-withdrawal-order-number,
        .vt-withdrawal-receive-amount {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-withdrawal-status-message {
          font-size: 13px;
          padding: 8px 12px;
          border-radius: 8px;
          margin-top: 2px;
        }
        
        .vt-withdrawal-status-message.approved {
          background: rgba(52, 211, 153, 0.1);
          color: #34d399;
          border-left: 3px solid #34d399;
        }
        
        .vt-withdrawal-status-message.pending {
          background: rgba(251, 191, 36, 0.1);
          color: #fbbf24;
          border-left: 3px solid #fbbf24;
        }
        
        .vt-withdrawal-status-message.rejected {
          background: rgba(248, 113, 113, 0.1);
          color: #f87171;
          border-left: 3px solid #f87171;
        }
        
        .vt-withdrawal-chevron {
          color: rgba(255, 255, 255, 0.3);
          font-size: 12px;
          margin-top: 8px;
        }
        
        /* Empty State */
        .vt-withdrawal-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 60px 20px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          border: 1px dashed rgba(255, 255, 255, 0.1);
        }
        
        .vt-withdrawal-empty-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 70px;
          height: 70px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          color: rgba(255, 255, 255, 0.3);
          margin-bottom: 16px;
        }
        
        .vt-withdrawal-empty-title {
          font-size: 18px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 8px;
        }
        
        .vt-withdrawal-empty-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          max-width: 300px;
          margin: 0;
          line-height: 1.5;
        }
        
        /* Loading State */
        .vt-withdrawal-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }
        
        .vt-withdrawal-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(126, 58, 242, 0.1);
          border-top: 3px solid #7e3af2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .vt-withdrawal-loading-text {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        /* Error State */
        .vt-withdrawal-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 60px 20px;
          background: rgba(239, 68, 68, 0.05);
          border-radius: 12px;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        
        .vt-withdrawal-error-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 50%;
          color: #f87171;
          margin-bottom: 16px;
        }
        
        .vt-withdrawal-error-title {
          font-size: 18px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 8px;
        }
        
        .vt-withdrawal-error-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 16px;
        }
        
        .vt-withdrawal-retry-button {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .vt-withdrawal-retry-button:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
        }
        
        .vt-withdrawal-no-results {
          display: flex;
          justify-content: center;
          padding: 40px 0;
          color: rgba(255, 255, 255, 0.6);
          font-size: 15px;
        }
        
        /* Responsive adjustments */
        @media (max-width: 600px) {
          .vt-withdrawal-details-grid {
            grid-template-columns: 1fr;
            gap: 6px;
          }
          
          .vt-withdrawal-summary-card {
            flex-direction: column;
            gap: 12px;
          }
          
          .vt-withdrawal-status-filters {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
};

export default WithdrawalRecord;